import { localize } from '../i18n';
import React, { useRef, useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FamilyHomeCard from './FamilyHomeCard';
import { analyticsId, trackProduct } from '../utils/productAnalytics';
import useAppLanguage from '../i18n';

const AUTO_ROTATE_MS = 5000;
export default function DiscoveryCarousel({ slides, topSpacing = 0.75 }) {
    const [idx, setIdx] = useState(0);
    const [visible, setVisible] = useState(document.visibilityState !== 'hidden');
    const [inView, setInView] = useState(false);
    const [hover, setHover] = useState(false);
    const root = useRef(null);
    const start = useRef(null);
    const swiped = useRef(false);
    const session = useRef(analyticsId());
    const seen = useRef(new Set());
    const ids = useRef(new Map());
    const count = slides?.length || 0;
    const current = count ? Math.min(idx, count - 1) : 0;
    const slide = slides?.[current];
    const key = slide ? `${slide.promoId || slide.state}:${slide.creativeVersion || 'v2'}:${current}` : '';
    if (key && !ids.current.has(key)) ids.current.set(key, analyticsId());
    const detail = slide ? { sessionId: session.current, promoId: slide.promoId, creativeVersion: slide.creativeVersion || 'v2', position: current, impressionId: ids.current.get(key), feature: slide.feature, source: 'home' } : null;
    const latest = useRef(detail);
    latest.current = detail;

    useEffect(() => {
        const handler = () => setVisible(document.visibilityState !== 'hidden');
        document.addEventListener('visibilitychange', handler);
        if (!window.IntersectionObserver || !root.current) return () => document.removeEventListener('visibilitychange', handler);
        const observer = new IntersectionObserver(entries => setInView(entries[0]?.intersectionRatio >= 0.5), { threshold: [0, 0.5, 1] });
        observer.observe(root.current);
        return () => { observer.disconnect(); document.removeEventListener('visibilitychange', handler); };
    }, [count > 0]);
    useEffect(() => {
        if (!visible || !inView || !key || seen.current.has(key) || !latest.current?.promoId) return;
        const timeout = setTimeout(() => { seen.current.add(key); trackProduct('home_promo_impression', latest.current); }, 1000);
        return () => clearTimeout(timeout);
    }, [visible, inView, key]);
    useEffect(() => {
        if (count < 2 || hover || !visible || !inView) return;
        const timeout = setTimeout(() => setIdx((current + 1) % count), AUTO_ROTATE_MS);
        return () => clearTimeout(timeout);
    }, [current, count, hover, visible, inView]);
    if (!slide) return null;
    const move = (next, source) => {
        if (detail?.promoId) trackProduct('home_promo_navigate', { ...detail, source });
        setIdx((next + count) % count);
    };
    const click = () => {
        if (swiped.current) return;
        if (detail?.promoId) trackProduct('home_promo_click', detail);
        slide.onClick?.();
    };
    return (
        <Box ref={root} role="region" aria-label={localize("도움이 되는 기능 안내")} aria-roledescription={localize("캐러셀")} sx={{ mx: 2, mt: topSpacing }}>
            <Box onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                onKeyDown={e => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); move(current + (e.key === 'ArrowLeft' ? -1 : 1), 'keyboard'); } }}
                onTouchStart={e => { swiped.current = false; start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
                onTouchCancel={() => { start.current = null; swiped.current = false; }}
                onTouchEnd={e => {
                    if (!start.current) return;
                    const dx = e.changedTouches[0].clientX - start.current.x;
                    const dy = e.changedTouches[0].clientY - start.current.y;
                    start.current = null;
                    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
                        swiped.current = true; e.preventDefault(); e.stopPropagation(); move(current + (dx < 0 ? 1 : -1), 'swipe');
                        setTimeout(() => { swiped.current = false; }, 0);
                    }
                }} onClickCapture={e => { if (swiped.current) { e.preventDefault(); e.stopPropagation(); } }} sx={{ touchAction: 'pan-y' }}>
                <FamilyHomeCard {...slide} onClick={click} />
            </Box>
            {count > 1 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                <IconButton aria-label={localize('이전 안내')} onClick={() => move(current - 1, 'indicator')} sx={{ minWidth: 44, minHeight: 44 }}><ChevronLeftRoundedIcon sx={{ fontSize: 30 }} /></IconButton>
                <Typography aria-live="off" sx={{ fontSize: '0.8rem', color: '#666' }}>{current + 1} / {count}</Typography>
                <IconButton aria-label={localize('다음 안내')} onClick={() => move(current + 1, 'indicator')} sx={{ minWidth: 44, minHeight: 44 }}><ChevronRightRoundedIcon sx={{ fontSize: 30 }} /></IconButton>
            </Box>}
        </Box>
    );
}
